import { supplierStockCost } from '@/lib/supplier-stock-cost';
import React, { useEffect, useRef, useState } from 'react';
import { storableTaxLineType } from '@/lib/sales/tax-line-type';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Form } from '@/components/atoms/form';
import { TooltipProvider } from '@/components/atoms/tooltip';
import { FormPageLayout } from '@/components/layouts';
import { FormSidebar, FormSidebarSection, UnsavedChangesDialog } from '@/components/molecules';
import { useItemSuppliers } from '@/hooks/pricebook/useSuppliers';
import { usePricebookItem } from '@/hooks/pricebook/usePricebookItem';
import {
  useCreateEquipmentDescription,
  useEquipmentDescription,
  useUpdateEquipmentDescription,
} from '@/hooks/equipment/useEquipmentDescriptions';
import {
  EQUIPMENT_SPEC_FIELD_NAMES,
  ensureEquipmentType,
  equipmentSpecFieldsFromWatched,
  equipmentSpecPendingFor,
  equipmentTypeFormFields,
  equipmentTypeSpecFields,
  hasEquipmentSpecContent,
  newEquipmentTypeSpec,
} from '@/lib/pricebook/equipment-type';
import { useUpdateItem } from '@/hooks/pricebook/useUpdateItem';
import { useUploadPricebookImage } from '@/hooks/pricebook/useUploadPricebookImage';
import { useDynamicServiceSummary } from '@/hooks/pricebook/useDynamicServiceSummary';
import { useImagePreview } from '@/hooks/useImagePreview';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import {
  ITEM_TYPE_LABELS,
  itemTypeCanHaveSuppliers,
  priceStrategyForItemType,
} from '@/types/pricebook';
import type {
  ServiceComponent,
  PricebookItem,
  PriceStrategy,
  UpsellItem,
  UpdateItemDto,
  DynamicServicePricingConfig,
  ServiceLaborLine,
} from '@/types/pricebook';
import { ItemTypeBadge } from '@/components/organisms/pricebook/ItemTypeBadge';
import {
  PricebookItemAdvancedSections,
  PricebookItemMainSection,
  type PricebookComponentItemCache,
} from '@/components/organisms/pricebook/PricebookItemFormSections';
import { useComponentTotals } from '@/hooks/pricebook/useComponentTotals';
import { MembershipPricingBreakdown } from '@/components/organisms/pricebook/MembershipPricingBreakdown';
import {
  flatServiceCostBreakdown,
  summedBundlePrice,
} from '@/components/organisms/pricebook/support/pricebook-bundle';
import { previewOptionalAdjustments } from '@/lib/sales/dynamic-service-price';
import { flatServiceLaborLines } from '@/lib/sales/service-labor';
import { Switch } from '@/components/atoms/switch';
import {
  laborLinesHours,
  laborLinesTotal,
} from '@/components/organisms/pricebook/LaborLinesEditor';
import {
  useApplyDynamicPrice,
  useDynamicPricePreview,
  useLaborRateProfiles,
} from '@/hooks/pricebook/useDynamicPricing';
import { DependencyChangeDialog } from '@/components/organisms/pricebook/DependencyChangeDialog';
import { DocumentCostPanel } from '@/components/organisms/sales/DocumentCostPanel';
import { PricebookBundleSummary } from '@/components/organisms/pricebook/PricebookBundleSummary';
import {
  AdvancedServiceAdjustments,
  DynamicServicePricingEditor,
} from '@/components/organisms/pricebook/DynamicServicePricingEditor';
import { ProfitUpliftEditor } from '@/components/organisms/pricebook/ProfitUpliftEditor';
import { sanitizeIdList } from '@/components/organisms/pricebook/support/id-lists';
import { pricebookDescriptionSchema } from '@/components/organisms/pricebook/support/description-validation';
import {
  equipmentCategoryRequirement,
  pricebookItemChecklist,
} from '@/components/organisms/pricebook/support/item-checklist';
import { computePricebookSectionOpenState } from '@/components/organisms/pricebook/support/section-open-state';
import {
  fmt,
  isFlatPricePricebookService,
  isTimeAndMaterialsPricebookItem,
  pricebookPrimaryPrice,
  pricebookPrimaryPriceLabel,
  pricebookPrimaryPriceSuffix,
} from '@/components/organisms/pricebook/support/pricebook-helpers';
import { isApiError } from '@/lib/api/errors';
import { applyServerErrorsToForm } from '@/lib/forms/apply-server-errors';
import { toServiceComponentPayloads } from '@/lib/pricebook/service-component-payload';
import { PricebookEditPage } from '@/components/organisms/pricebook/v2/PricebookEditPage';

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/pricebook/$id/edit')({
  component: EditPricebookItemV2Route,
});

/** The edit route now uses the same v2 form tree as item creation. */
function EditPricebookItemV2Route() {
  const { id } = Route.useParams();
  return <PricebookEditPage itemId={id} />;
}

// ─── Schema ───────────────────────────────────────────────────────────────────

const TAX_LINE_TYPES = ['flat_rate', 'labor', 'materials', 'freight', 'exempt'] as const;

// Exported so the route's validation contract can be unit-tested directly.
// eslint-disable-next-line react-refresh/only-export-components
export const itemSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(255, 'Name must be 255 characters or fewer'),
    code: z
      .string()
      .trim()
      .min(1, 'Code is required')
      .max(100, 'Code must be 100 characters or fewer'),
    description: pricebookDescriptionSchema.optional(),
    basePrice: z
      .number({ error: 'Base price must be a number' })
      .min(0, 'Base price cannot be negative')
      .default(0),
    laborRate: z
      .number({ error: 'Labor rate must be a number' })
      .min(0, 'Labor rate cannot be negative')
      .default(0),
    // SERVICE — what an hour COSTS us, and how many hours the work takes.
    // Cost only: neither ever moves the price. See SPEC-line-item-model,
    // "Flat vs Dynamic".
    laborCostRate: z
      .number({ error: 'Labor cost rate must be a number' })
      .min(0, 'Labor cost rate cannot be negative')
      .default(0),
    estimatedHours: z
      .number({ error: 'Hours must be a number' })
      .min(0, 'Hours cannot be negative')
      .default(0),
    laborCostLines: z
      .array(
        z.object({
          id: z.string().optional(),
          label: z.string().default(''),
          durationMinutes: z.number().min(0).default(0),
          rateSource: z.enum(['FIXED', 'PROFILE']).default('FIXED'),
          fixedRate: z.number().min(0).optional(),
          laborRateProfileId: z.string().optional(),
        }),
      )
      .default([]),
    costBasis: z
      .number({ error: 'Cost must be a number' })
      .min(0, 'Cost cannot be negative')
      .default(0),
    markupPercent: z
      .number({ error: 'Markup must be a number' })
      .min(-100, 'Markup cannot be lower than -100%')
      .default(0),
    // NOT-605 member pricing (per-item override). null = fall through.
    memberPrice: z
      .number({ error: 'Member price must be a number' })
      .min(0, 'Member price cannot be negative')
      .nullable()
      .default(null),
    memberDiscountPercent: z
      .number({ error: 'Member discount must be a number' })
      .min(0, 'Member discount cannot be negative')
      .max(100, 'Member discount cannot exceed 100%')
      .nullable()
      .default(null),
    currency: z.string().max(3, 'Use a 3-letter currency code (e.g. USD)').default('USD'),
    // COMPONENT_SUM belongs to bundles, which are edited through this same
    // form. Leaving it out of the enum made every save of a summed bundle
    // either fail to parse or come back as FLAT.
    priceStrategy: z.enum(['FLAT', 'DYNAMIC', 'COMPONENT_SUM']).default('FLAT'),
    unit: z.string().optional(),
    taxable: z.boolean().default(true),
    taxCategory: z.string().optional(),
    taxCode: z.string().optional(),
    taxLineType: z.union([z.enum(TAX_LINE_TYPES), z.literal('')]).default(''),
    glAccount: z.string().optional(),
    glClass: z.string().optional(),
    glCogs: z.string().optional(),
    glExpenseAccount: z.string().optional(),
    displayOrder: z
      .number({ error: 'Display order must be a number' })
      .min(0, 'Display order cannot be negative')
      .default(0),
    hidePrice: z.boolean().default(false),
    // NOT-590 — how the customer sees a service's included parts. Presentation
    // only: the same lines and the same total either way.
    componentDisplayMode: z.enum(['ITEMIZED', 'ROLLED_UP']).default('ROLLED_UP'),
    // Which detail the single customer-facing line shows.
    lineDetail: z.enum(['AUTO', 'DESCRIPTION', 'DESCRIPTION_AND_AUTO']).default('AUTO'),
    requirePhotos: z.boolean().default(false),
    requireAsset: z.boolean().default(false),
    aiTags: z.array(z.string()).default([]),
    requiredAssets: z.array(z.string()).default([]),
    equipmentDescriptionId: z.string().optional().or(z.literal('')),
    // Equipment spec + warranty. The backend stores these on the linked
    // EquipmentDescription, but they are edited here, on the item that prices
    // them — the same panels the new-item form shows. Declared on the schema
    // because `forbidNonWhitelisted`'s local equivalent applies here too: a
    // field zod doesn't know is a field the form silently drops on save.
    equipmentCategoryKey: z.string().optional().or(z.literal('')),
    brand: z.string().max(120).optional().or(z.literal('')),
    equipmentModel: z.string().max(120).optional().or(z.literal('')),
    manufacturer: z.string().max(120).optional().or(z.literal('')),
    mpn: z.string().max(120).optional().or(z.literal('')),
    upc: z.string().max(120).optional().or(z.literal('')),
    lengthIn: z.number().nullable().default(null),
    widthIn: z.number().nullable().default(null),
    heightIn: z.number().nullable().default(null),
    weightLbs: z.number().nullable().default(null),
    capacity: z.string().max(120).optional().or(z.literal('')),
    fuelType: z.string().max(120).optional().or(z.literal('')),
    efficiencyRating: z.string().max(120).optional().or(z.literal('')),
    refrigerantType: z.string().max(120).optional().or(z.literal('')),
    filterSize: z.string().max(120).optional().or(z.literal('')),
    expectedLifespanYears: z.number().nullable().default(null),
    defaultManufacturerWarrantyMonths: z.number().nullable().default(null),
    defaultManufacturerWarrantyTerms: z.string().max(2000).optional().or(z.literal('')),
    defaultServiceProviderWarrantyMonths: z.number().nullable().default(null),
    defaultServiceProviderWarrantyTerms: z.string().max(2000).optional().or(z.literal('')),
    isConfigurable: z.boolean().default(false),
    configurations: z
      .array(
        z.object({
          name: z.string().trim().min(1, 'Configuration name is required').max(120),
          price: z.number().min(0).optional(),
          attributes: z.record(z.string(), z.unknown()).optional(),
        }),
      )
      .default([]),
    hazardLevel: z
      .number({ error: 'Hazard level must be a number' })
      .min(0, 'Hazard level must be between 0 and 5')
      .max(5, 'Hazard level must be between 0 and 5')
      .default(0),
    retrievalBoost: z
      .number({ error: 'Retrieval boost must be a number' })
      .min(0, 'Retrieval boost cannot be negative')
      .default(1),
    membershipPlanId: z.string().optional(),
    membershipGrantTierId: z.string().optional(),
    membershipPlanIds: z.array(z.string()).default([]),
  })
  .refine(
    // The toggle is only shown for equipment; if it's on, require a configuration.
    (val) => !val.isConfigurable || val.configurations.length > 0,
    {
      path: ['configurations'],
      message: 'Add at least one configuration for a configurable equipment item.',
    },
  )
  // The chosen configuration's NAME is what identifies a configured line (see
  // lineIdentityKey and the server's assertConfigurableLinesResolved), so two
  // configurations sharing a name would be indistinguishable once sold.
  .refine(
    (val) => {
      if (!val.isConfigurable) return true;
      const names = val.configurations.map((c) => c.name.trim().toLowerCase());
      return new Set(names).size === names.length;
    },
    {
      path: ['configurations'],
      message: 'Configuration names must be unique.',
    },
  )
  // The new-item form's category rule, enforced here through the resolver too
  // (NOT-641). It used to be an imperative check inside onSubmit, which meant a
  // toast and nothing else: the field was never marked, the form never scrolled
  // to it, and the toast faded while the typed values sat there looking saved.
  //
  // Same rule as the new-item form — literally, `equipmentCategoryRequirement`
  // — but fed differently, because this schema sees less and owes more. It has
  // no `type` field (an item's type is fixed at creation) and it must not brick
  // an item that predates the rule: an equipment item saved with no linked type
  // can still have its price changed or be archived. So what decides whether a
  // type gets written here is what the operator has actually typed. A form with
  // something to write onto an equipment type has to say which category that
  // type is; a form with empty spec panels has nothing to write and is left
  // alone. Non-equipment items never render those panels, so they never trip it.
  //
  // The sidebar checklist reads the same predicate off the same inputs, so it
  // cannot claim a category is missing on an edit this refine lets through.
  .refine(
    (val) =>
      equipmentCategoryRequirement({
        // This schema has no `type`. Only equipment renders the spec panels, so
        // a service's are always empty and `writesEquipmentType` is false for
        // it — the rule stays off non-equipment items without being told which
        // ones they are.
        isEquipment: true,
        equipmentDescriptionId: val.equipmentDescriptionId,
        equipmentCategoryKey: val.equipmentCategoryKey,
        writesEquipmentType: hasEquipmentSpecContent(val),
      }) !== 'missing',
    {
      path: ['equipmentCategoryKey'],
      message: 'Choose an equipment category — the equipment type is created from it.',
    },
  );

type ItemFormValues = z.input<typeof itemSchema>;

/** Human-readable labels for surfacing field-level validation errors (NOT-49). */
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  code: 'Code',
  description: 'Description',
  basePrice: 'Base price',
  laborRate: 'Labor rate',
  laborCostRate: 'Labor cost rate',
  estimatedHours: 'Hours',
  costBasis: 'Cost',
  markupPercent: 'Markup',
  currency: 'Currency',
  unit: 'Unit',
  taxCategory: 'Tax category',
  taxCode: 'Tax code',
  taxLineType: 'Tax line type',
  glAccount: 'GL account',
  glClass: 'GL class',
  glCogs: 'GL COGS',
  glExpenseAccount: 'GL expense account',
  displayOrder: 'Display order',
  hazardLevel: 'Hazard level',
  retrievalBoost: 'Retrieval boost',
  equipmentDescriptionId: 'Equipment description',
  equipmentCategoryKey: 'Equipment category',
  configurations: 'Configurations',
  membershipPlanId: 'Membership plan',
};

/** Flatten RHF field errors into `Label: message` strings for a toast. */
// eslint-disable-next-line react-refresh/only-export-components
export function collectFieldIssues(errors: FieldErrors<ItemFormValues>): string[] {
  return Object.entries(errors)
    .map(([field, err]) => {
      const message = (err as { message?: unknown } | undefined)?.message;
      if (typeof message !== 'string' || !message) return null;
      return `${FIELD_LABELS[field] ?? field}: ${message}`;
    })
    .filter((issue): issue is string => issue !== null);
}

function toNullableString(value?: string): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function toOptionalString(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

// Edit PUTs are partial updates, so an omitted array means "leave the stored
// value alone." Keep these fields present even when the user removes the last
// chip; [] is the explicit instruction to clear the persisted list.
// eslint-disable-next-line react-refresh/only-export-components
export function buildAiSearchUpdateFields(
  values: Pick<ItemFormValues, 'aiTags' | 'requiredAssets'>,
): Required<Pick<UpdateItemDto, 'aiTags' | 'requiredAssets'>> {
  return {
    aiTags: values.aiTags ?? [],
    requiredAssets: values.requiredAssets ?? [],
  };
}

/**
 * The dynamic-pricing config the form starts from, given what the catalog
 * stored.
 *
 * A legacy Time & Materials service — one priced by a single `laborRate`
 * before dynamic pricing existed — has no stored config, and OPENING it must
 * not invent one. The editor already renders a blank labor row of its own when
 * `laborLines` is empty, so seeding one here bought nothing and cost
 * everything: the synthetic row travelled into the save payload, the server
 * read the accompanying `enabled: true` as "this service is priced
 * dynamically", and zeroed the item's `laborRate` — turning an edit to some
 * unrelated field (a rename, a tax code) into a silent rewrite of how the
 * service prices, at $0.
 *
 * Dynamic pricing is opt-in. The opt-in is the user actually touching the
 * dynamic editor, which is what sets `enabled` (see
 * `DynamicServicePricingEditor`), not the act of opening the page.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function hydrateDynamicPricingConfig(
  stored: DynamicServicePricingConfig | null | undefined,
): DynamicServicePricingConfig {
  return stored ?? {};
}

/**
 * Whether this service is priced by the dynamic pricebook.
 *
 * `enabled` is the one flag the rest of the system keys on — the server's
 * dynamic-pricing preview refuses to compute without it, and the pricing engine
 * decides from it whether a document line's frozen `basePrice` is the whole
 * price. Answering the question any other way (a non-empty `laborLines`, say)
 * is how the form and the biller come to disagree about what a service costs.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usesDynamicServicePricing(
  config: DynamicServicePricingConfig | null | undefined,
): boolean {
  return config?.enabled === true;
}

/**
 * The form's starting values for an item already in the catalog.
 *
 * Exported for test: what the form starts from is half of what it saves, and a
 * strategy dropped here is a strategy rewritten on the next save.
 */
/**
 * The `basePrice` an UPDATE should send — or nothing at all.
 *
 * A DYNAMIC service's price is DERIVED and frozen by the dynamic pricing
 * engine, and this form does not even show the Price field for one (see
 * `showBasePrice`), so the form's `basePrice` is a stale default rather than an
 * operator's answer.
 *
 * Sending it as an explicit `0` was worse than meaningless. The backend
 * reconciles a dynamic service with `dto.basePrice ?? existing.basePrice`,
 * where an explicit `0` WINS — so renaming a dynamic service, or editing its
 * description, zeroed the materialized price and every later estimate and
 * invoice repriced from nothing.
 *
 * `undefined` is the true statement: this save has nothing to say about the
 * price. Materializing the dynamic price is a different call that sends its own
 * figure.
 *
 * Exported for test: what a save omits is as load-bearing as what it sends, and
 * there is no way to see an absent field through the rendered form.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function basePriceForUpdate(args: {
  isService: boolean;
  priceStrategy?: string;
  basePrice: number;
}): number | undefined {
  return args.isService && args.priceStrategy === 'DYNAMIC' ? undefined : args.basePrice;
}

/**
 * The operator's answer to "a part this service is priced from has changed".
 *
 * Deliberately an argument to a single save and NOT state the editor carries.
 * A decision that outlives the save it was given for goes on answering
 * questions nobody asked it: decline a part's new cost, then edit a labor line
 * and save, and the labor edit is silently left unpriced too. `KEEP` declines
 * one set of changes, once.
 */
export type DependencyDecision = 'ASK' | 'REPRICE' | 'KEEP';

/**
 * What saving a dynamic service should do about the price it is priced AT.
 *
 * Two different events reach this form as the same click.
 *
 * The operator configures labor, parts and adjustments, watches the panel total
 * them, and saves. That price has to be written down — it is derived, so the
 * save itself says nothing about it (`basePriceForUpdate`), and without the
 * recalculation the catalog goes on listing whatever the service cost before,
 * against a cost of $0.
 *
 * Or a MATERIAL got more expensive somewhere else in the pricebook, and every
 * service consuming it is quietly worth more. Recalculating on save would adopt
 * that too — renaming the service would be enough to reprice it, which is the
 * complaint this exists to answer. So when something really has moved
 * underneath, it is stated and asked once, and the answer decides.
 *
 * Exported for test: the difference between these two is invisible in the
 * rendered form — both are one Save click — and getting it wrong is either a
 * price that will not save or one that changes behind the operator's back.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function dynamicSavePlan(args: {
  isService: boolean;
  priceStrategy?: string;
  /** The service is actually priced by the dynamic pricebook, not legacy T&M. */
  usesDynamicPricing: boolean;
  /** Inputs edited elsewhere since this service's price was last applied. */
  dependencyChangeCount: number;
  /**
   * The server's own answer to whether a stored figure is out of date.
   *
   * Asked TOGETHER with the changes above, because they answer different
   * questions: a dependency can be edited without moving this service at all —
   * a markup profile retuned in a band none of its parts fall in. Stopping the
   * save for one of those would contradict the item's own page, which shows no
   * banner, over a recalculation that would change nothing.
   */
  pricingIsStale: boolean;
  decision: DependencyDecision;
}): { isDynamic: boolean; ask: boolean; reprice: boolean } {
  const isDynamic = args.isService && args.priceStrategy === 'DYNAMIC' && args.usesDynamicPricing;
  const drifted = isDynamic && args.pricingIsStale && args.dependencyChangeCount > 0;
  const ask = drifted && args.decision === 'ASK';
  return {
    isDynamic,
    ask,
    // KEEP is the operator declining the new figures: everything else about the
    // item still saves, and the pricebook goes on offering the update on the
    // item's own page.
    reprice: isDynamic && !ask && args.decision !== 'KEEP',
  };
}

/**
 * What a save may say about cost.
 *
 * Nothing, once a primary supplier owns it. There is no cost input on this form
 * for a material or equipment — the figure is a read-only mirror of the
 * supplier's — so sending it can never carry an operator's answer, only a copy
 * of one that may already be out of date. That copy going back over a cost just
 * changed on the supplier's row is exactly how an updated cost silently
 * reverted, since the two requests are one click apart (blur, then submit).
 *
 * Nothing for a DYNAMIC service either, and for the same reason `basePrice`
 * says nothing (see `basePriceForUpdate`): its cost is DERIVED — the labor it
 * bills plus what its parts cost — and apply-price is what writes it. This used
 * to send an explicit `0`, which won over the stored figure, so every save
 * wiped the cost the apply had just calculated and left the service reporting
 * 100% margin.
 *
 * Exported for test: what a save omits is as load-bearing as what it sends, and
 * there is no way to see an absent field through the rendered form.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function costBasisForUpdate(args: {
  itemType: PricebookItem['type'];
  priceStrategy?: string;
  supplierOwned: boolean;
  costBasis: number;
}): number | undefined {
  const isService = args.itemType === 'service';
  if (isService && args.priceStrategy === 'DYNAMIC') return undefined;
  if (args.itemType === 'material' || args.itemType === 'equipment') {
    // A primary supplier already synchronises the cached item cost. Without
    // one, explicitly clear any legacy hand-entered cost instead of preserving
    // a number with no supplier behind it.
    return args.supplierOwned ? undefined : 0;
  }
  return isService ? args.costBasis : undefined;
}

// eslint-disable-next-line react-refresh/only-export-components
export function deriveItemFormValues(item: PricebookItem): ItemFormValues {
  const isService = item.type === 'service';
  const isTimeAndMaterials = isService && item.priceStrategy === 'DYNAMIC';
  const isMaterial = item.type === 'material' || item.type === 'equipment';

  return {
    name: item.name,
    code: item.sku ?? '',
    description: item.description ?? '',
    basePrice: isTimeAndMaterials ? 0 : (item.price ?? 0),
    laborRate: isTimeAndMaterials ? (item.laborRate ?? 0) : 0,
    // Cost, not price — kept for BOTH modes. Blanking it on the dynamic side
    // would make flipping a service back to flat lose what its hours cost.
    laborCostRate: item.laborCostRate ?? 0,
    estimatedHours: item.estimatedHours ?? 0,
    laborCostLines: item.laborCostLines ?? [],
    costBasis: isMaterial || (isService && !isTimeAndMaterials) ? (item.cost ?? 0) : 0,
    markupPercent: isMaterial ? (item.markupPercent ?? 0) : 0,
    memberPrice: item.memberPrice ?? null,
    memberDiscountPercent: item.memberDiscountPercent ?? null,
    currency: item.currency ?? 'USD',
    // Start from what the catalog stored, clamped to what this item's type can
    // legally be. A bundle saved as COMPONENT_SUM must come back as
    // COMPONENT_SUM or opening the item is enough to reprice it.
    priceStrategy: priceStrategyForItemType(item.type, item.priceStrategy),
    unit: isTimeAndMaterials ? 'HR' : (item.unit ?? 'EA'),
    taxable: item.taxable ?? true,
    taxCategory: item.taxCategory ?? 'DEFAULT',
    taxCode: item.taxCode ?? '',
    taxLineType: item.taxLineType ?? '',
    glAccount: item.glAccount ?? '',
    glClass: item.glClass ?? '',
    glCogs: item.glCogs ?? '',
    glExpenseAccount: item.glExpenseAccount ?? '',
    displayOrder: item.displayOrder ?? 0,
    hidePrice: item.displayConfig?.hidePrice ?? false,
    // An item saved before this setting existed states nothing, and every
    // renderer prints its description AND its parts for that. The control has
    // to preselect something, so it preselects the option that prints exactly
    // that — NOT 'AUTO', which would have quietly dropped the description from
    // the customer's line the first time anybody saved an old item.
    lineDetail: item.displayConfig?.lineDetail ?? 'DESCRIPTION_AND_AUTO',
    componentDisplayMode: item.componentDisplayMode ?? 'ROLLED_UP',
    requirePhotos: item.displayConfig?.requirePhotos ?? false,
    requireAsset: item.displayConfig?.requireAsset ?? false,
    aiTags: item.aiTags ?? [],
    requiredAssets: item.requiredAssets ?? [],
    equipmentDescriptionId: item.equipmentDescriptionId ?? '',
    isConfigurable: item.type === 'equipment' ? (item.isConfigurable ?? false) : false,
    configurations: item.type === 'equipment' ? (item.configurations ?? []) : [],
    hazardLevel: item.hazardLevel ?? 0,
    retrievalBoost: item.retrievalBoost ?? 1,
    membershipPlanId: item.membershipConfig?.planId ?? '',
    membershipGrantTierId: item.membershipConfig?.grantTierId ?? '',
    membershipPlanIds: item.membershipPlanIds ?? [],
  };
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/** @deprecated Kept temporarily for exported regression helpers; no route renders this UI. */
function LegacyEditPricebookItemPage() {
  const businessUnitsQuery = useBusinessUnits('active');
  const businessUnits = businessUnitsQuery.data ?? [];
  const hasNoBusinessUnits = businessUnitsQuery.isSuccess && businessUnits.length === 0;
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const itemQuery = usePricebookItem(id);
  const updateItem = useUpdateItem();
  const uploadImage = useUploadPricebookImage();

  const item = itemQuery.data;
  const createEquipmentDescription = useCreateEquipmentDescription();
  const updateEquipmentDescription = useUpdateEquipmentDescription();
  // The spec behind an equipment item, so the panels can show what it IS
  // instead of a set of empty boxes over a type that already has the answers.
  const equipmentTypeQuery = useEquipmentDescription(
    item?.equipmentDescriptionId ?? undefined,
    item?.type === 'equipment' && !!item?.equipmentDescriptionId,
  );
  const hydratedItemIdRef = useRef<string | null>(null);
  const hydratedEquipmentTypeIdRef = useRef<string | null>(null);
  // A type this form created for a link-less item, kept so a failed item save
  // can be retried without stranding — or colliding with — the one it left.
  const createdEquipmentTypeRef = useRef<string | undefined>(undefined);
  const formRef = useRef<HTMLFormElement>(null);

  const [components, setComponents] = useState<ServiceComponent[]>([]);
  /**
   * The id whose members have been seeded into `components`.
   *
   * State rather than a ref, because the summed-bundle price effect has to
   * re-run the moment it lands — a ref changes nothing and the derived price
   * would sit stale until some other dependency happened to move. Keyed by id
   * because this route does not remount between `:id` navigations, so a bare
   * boolean would still read as hydrated for the NEXT item.
   */
  const [componentsHydratedFor, setComponentsHydratedFor] = useState<string | null>(null);
  // Empty until the item is hydrated. An edit form must never start from a
  // config the stored item does not have — see `hydrateDynamicPricingConfig`.
  const [dynamicPricingConfig, setDynamicPricingConfig] = useState<DynamicServicePricingConfig>({});
  const [componentItemCache, setComponentItemCache] = useState<PricebookComponentItemCache>({});
  const [upsellItems, setUpsellItems] = useState<UpsellItem[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  // Browse folders. Order is meaningful — the first is the primary folder.
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const {
    pendingFile: pendingImageFile,
    preview: imagePreview,
    selectImage,
    clearImage,
    setRemotePreview,
  } = useImagePreview();
  const [basicInfoOpen, setBasicInfoOpen] = useState(true);
  const [componentsSectionOpen, setComponentsSectionOpen] = useState(true);
  const [membershipLinkOpen, setMembershipLinkOpen] = useState(true);
  const [taxOpen, setTaxOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [displayOpen, setDisplayOpen] = useState(false);

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      basePrice: 0,
      memberPrice: null,
      memberDiscountPercent: null,
      laborRate: 0,
      laborCostRate: 0,
      estimatedHours: 0,
      laborCostLines: [],
      costBasis: 0,
      markupPercent: 0,
      currency: 'USD',
      priceStrategy: 'FLAT',
      unit: 'EA',
      taxable: true,
      taxCategory: 'DEFAULT',
      taxCode: '',
      taxLineType: '',
      glAccount: '',
      glClass: '',
      glCogs: '',
      glExpenseAccount: '',
      displayOrder: 0,
      hidePrice: false,
      lineDetail: 'AUTO' as const,
      componentDisplayMode: 'ROLLED_UP' as const,
      requirePhotos: false,
      requireAsset: false,
      aiTags: [],
      requiredAssets: [],
      equipmentDescriptionId: '',
      isConfigurable: false,
      configurations: [],
      hazardLevel: 0,
      retrievalBoost: 1,
      membershipPlanId: '',
      membershipGrantTierId: '',
      membershipPlanIds: [],
    },
  });

  // The item links a type whose spec has not reached the panels yet — still in
  // flight, or its request failed. Either way the panels are showing blanks
  // that are not the truth, so they are locked and the save refuses to write
  // them rather than reporting success over an edit it dropped.
  // Read off the query, not the hydration ref: a ref changing does not
  // re-render, so the panels would stay locked after the type arrived.
  const equipmentSpecPending =
    item?.type === 'equipment' &&
    equipmentSpecPendingFor(item.equipmentDescriptionId, equipmentTypeQuery.data?.id);

  // A dynamic service's price and cost are DERIVED, so saving one has to end by
  // recalculating them — the panel on the right is otherwise the only place the
  // new figure ever exists, and the catalog keeps listing whatever it was
  // before. The same call the create page makes the moment the item exists.
  const applyDynamicPrice = useApplyDynamicPrice({ notify: false });
  // The recalculation is part of the save, so the button stays busy through it.
  const isMutating = updateItem.isPending || uploadImage.isPending || applyDynamicPrice.isPending;
  // Edits are real work: leaving the page — Back, Cancel, the tab — must ask
  // first. A save in flight is exempt; it is the reason we are leaving.
  const unsavedChanges = useUnsavedChangesGuard({
    when: form.formState.isDirty && !isMutating,
  });

  const watchedTaxable = form.watch('taxable');
  const watchedName = form.watch('name');
  const watchedCode = form.watch('code');
  const watchedBasePrice = form.watch('basePrice');
  const watchedLaborRate = form.watch('laborRate');
  const watchedPriceStrategy = form.watch('priceStrategy');
  const watchedDescription = form.watch('description');
  const watchedMembershipPlanId = form.watch('membershipPlanId');
  const watchedEquipmentCategoryKey = form.watch('equipmentCategoryKey');
  const watchedEquipmentDescriptionId = form.watch('equipmentDescriptionId');
  // The category is owed only when this save will write an equipment type, and
  // here that is decided by what sits in the spec panels — so the sidebar has
  // to read them live, exactly as the refine does. Subscribing to those fields
  // and no others keeps the rest of this very large form off the render path.
  const watchedEquipmentSpec = form.watch(EQUIPMENT_SPEC_FIELD_NAMES);
  const hasTypedEquipmentSpec = hasEquipmentSpecContent(
    equipmentSpecFieldsFromWatched(watchedEquipmentSpec),
  );
  // How the service's price and its parts combine is the pricing strategy. A
  // time-and-materials service lists an hourly rate that parts do not add to; a
  // flat-price service sells for the price typed here, with the parts as cost.
  const isTimeAndMaterials = isTimeAndMaterialsPricebookItem({
    type: item?.type,
    priceStrategy: watchedPriceStrategy,
  });
  const isFlatPriceService = isFlatPricePricebookService({
    type: item?.type,
    priceStrategy: watchedPriceStrategy,
  });
  const isBundle = item?.type === 'bundle';
  /** A bundle priced from its members rather than by a figure the operator typed. */
  const isSummedBundle = isBundle && watchedPriceStrategy === 'COMPONENT_SUM';
  // The item's OWN price, on every path but time-and-materials — whose listed
  // figure is an hourly rate that parts do not add to.
  //
  // A bundle's price used to be forced to 0 here so that `serviceAmount +
  // partsAmount` would land on the members' sum. That worked for the summed
  // bundle by cancellation and left the FLAT one double-counting: a $1,131.81
  // bundle holding $1,131.81 of members totalled $2,263.62. The rule now lives
  // where it belongs (`isBundle` below), so the price can be stated honestly.
  const bundleServicePrice = isTimeAndMaterials ? 0 : Number(watchedBasePrice || 0);
  // The pricing defaults go in because a part row that names no profile of its
  // own falls back to them — without that the summary priced every part at its
  // catalog price and then reported a markup the operator had declined.
  const componentTotals = useComponentTotals(
    components,
    componentItemCache,
    bundleServicePrice,
    dynamicPricingConfig,
    isFlatPriceService,
    isBundle,
  );

  // THE PRICE FOLLOWS THE CONTENTS, which is the whole of what
  // "Add up what is in the bundle" promises.
  //
  // Kept in the form field rather than derived at save time so the operator
  // SEES it move: they change a member's quantity, or flip one between its cost
  // and its price, and the figure beside it answers. Nothing wrote it before,
  // so the price sat at whatever had been typed however the bundle changed —
  // and the backend, which does derive it, then disagreed with the screen.
  //
  // `null` while a member is still resolving: a sum missing a member is worse
  // than the stale one, because nothing marks it as provisional.
  useEffect(() => {
    if (!isSummedBundle) return;
    // An empty member list means one of two OPPOSITE things, and the previous
    // guard — refuse to write whenever there are no members — answered only
    // the first:
    //
    //   NOT LOADED YET. Writing a sum over nothing here would zero a saved
    //   bundle's price the moment its edit form opened.
    //
    //   EMPTIED BY THE OPERATOR. Refusing to write here left the removal of
    //   the LAST member showing the price of a bundle that no longer contains
    //   it — remove a $400 member and the field keeps saying $400, which is
    //   the opposite of "add up what is in the bundle".
    //
    // Hydration is what tells them apart, so that is what is checked. After
    // this item's form has been seeded, empty is the operator's doing and the
    // price follows it down to zero.
    if (componentsHydratedFor !== item?.id) return;
    const derived = summedBundlePrice(componentTotals);
    if (derived == null) return;
    if (Number(form.getValues('basePrice') ?? 0) === derived) return;
    form.setValue('basePrice', derived, { shouldDirty: true, shouldValidate: false });
  }, [form, isSummedBundle, componentTotals, componentsHydratedFor, item?.id]);
  // PREVIEW ONLY. After-hours and the add-on discount are conditional on the
  // JOB, so the catalog price excludes them — which left an operator able to
  // configure either and unable to see what it does short of selling one.
  // Nothing here is saved; the everyday price above is untouched.
  const [previewAfterHours, setPreviewAfterHours] = useState(false);
  const [previewAsAddOn, setPreviewAsAddOn] = useState(false);
  const dynamicSummary = useDynamicServiceSummary(
    dynamicPricingConfig,
    components,
    componentItemCache,
  );
  const adjustmentPreview = previewOptionalAdjustments({
    price: dynamicSummary.calculatedPrice,
    config: dynamicPricingConfig,
    afterHours: previewAfterHours,
    asAddOn: previewAsAddOn,
  });
  const usesDynamicPricing = usesDynamicServicePricing(dynamicPricingConfig);
  // What the SAVED service is worth against its parts as they stand now. The
  // dependency changes on it are the only thing that can tell an edit driven by
  // the operator apart from one a child material made underneath them.
  const dynamicPreview = useDynamicPricePreview(
    id,
    item?.type === 'service' && item.priceStrategy === 'DYNAMIC',
  );
  const dependencyChanges = dynamicPreview.data?.dependencyChanges ?? [];
  const pricingIsStale = dynamicPreview.data?.updateAvailable === true;
  // The save the dialog is holding, and the ONLY state the ask carries. The
  // answer itself is passed to the save it was given for and then forgotten —
  // see `DependencyDecision`.
  const [pendingSave, setPendingSave] = useState<ItemFormValues | null>(null);
  const showBundleTotal = components.length > 0 && !isTimeAndMaterials;
  // The labor the operator entered in "Labor used by this service", reduced the
  // same way the estimate reduces it — one function, so the catalog and the
  // document it becomes cannot report different costs for the same service.
  const { data: laborRateProfiles = [] } = useLaborRateProfiles();
  // Through the ONE reconstruction, the same function the labor editor above
  // uses. Reading `laborCostLines` raw printed Labor $0.00 for a service saved
  // before the rows existed — while the editor on the same screen showed the row
  // it rebuilt from `estimatedHours` / `laborCostRate`.
  const flatLaborLines = flatServiceLaborLines({
    laborCostLines: form.watch('laborCostLines') as ServiceLaborLine[] | undefined,
    estimatedHours: form.watch('estimatedHours') as number | undefined,
    laborCostRate: form.watch('laborCostRate') as number | undefined,
  });
  const flatServiceCost = flatServiceCostBreakdown(
    componentTotals,
    flatLaborLines,
    laborRateProfiles,
    Number(watchedBasePrice) || 0,
  );

  useEffect(() => {
    if (!item) return;
    if (hydratedItemIdRef.current === item.id) return;
    hydratedItemIdRef.current = item.id;
    // This route does not remount between :id navigations, and the reset below
    // blanks the spec panels — so the next item must seed its own, and must not
    // inherit a claim that the panels on screen came from a type.
    hydratedEquipmentTypeIdRef.current = null;
    createdEquipmentTypeRef.current = undefined;
    // A save held open for a confirmation belongs to the item it was typed
    // against. This route does not remount between :id navigations, so leaving
    // it would let the dialog answer for a service it was never about.
    setPendingSave(null);
    form.reset(deriveItemFormValues(item));
    // Unconditionally, INCLUDING the empty case. This route does not remount
    // between `:id` navigations, so seeding only when the next item has members
    // left the previous item's list on screen for one that has none — and the
    // summed-bundle price below would then be a sum of somebody else's members.
    setComponents(item.components ?? []);
    setComponentsHydratedFor(item.id);
    setDynamicPricingConfig(hydrateDynamicPricingConfig(item.dynamicPricingConfig));
    setUpsellItems(item.upsellItems ?? []);
    setTagIds(sanitizeIdList((item.tags ?? []).map((t) => t.id)));
    // The picker shows the FIRST id and nothing else, so the primary has to be
    // first. The server already orders it that way; sorting makes the picker
    // independent of that promise rather than quietly showing the wrong folder
    // if it ever changes. The sort is STABLE, so the remaining folders keep the
    // displayOrder the server sent them in.
    setCategoryIds(
      sanitizeIdList(
        [...(item.categories ?? [])]
          .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
          .map((c) => c.categoryId),
      ),
    );
    // NOT-36: auto-expand any advanced section that already carries data so the
    // user doesn't mistake collapsed (but populated) sections — especially the
    // AI Tags / Required Assets section — for lost data. Set the open state
    // EXPLICITLY (not just open-if-data): this route does not remount between
    // different :id navigations, so a bare `if (data) setOpen(true)` would leak
    // the previous item's expanded sections onto the next item. This effect is
    // guarded by hydratedItemIdRef (runs once per item id), so an explicit reset
    // per item still preserves the user's manual collapse/expand within an item.
    const sectionsWithData = computePricebookSectionOpenState(item);
    setAiOpen(sectionsWithData.aiOpen);
    setTaxOpen(sectionsWithData.taxOpen);
    setDisplayOpen(sectionsWithData.displayOpen);
    if (item.imagePreviewUrl && !pendingImageFile) {
      setRemotePreview(item.imagePreviewUrl);
    }
  }, [form, item, pendingImageFile, setRemotePreview]);

  // The spec panels are seeded from the linked type, which arrives on its own
  // request — so it lands after the reset above rather than in it.
  //
  // Seeded as the form's BASELINE, not as an edit: showing an item what it
  // already is must not arm the unsaved-changes guard. `keepDirtyValues` is
  // what makes that safe against the race — anything the operator had already
  // changed while the type was in flight stays changed and stays dirty.
  useEffect(() => {
    const type = equipmentTypeQuery.data;
    if (!item || !type || type.id !== item.equipmentDescriptionId) return;
    if (hydratedEquipmentTypeIdRef.current === type.id) return;
    hydratedEquipmentTypeIdRef.current = type.id;
    form.reset(
      { ...deriveItemFormValues(item), ...equipmentTypeFormFields(type) },
      { keepDirtyValues: true },
    );
  }, [form, item, equipmentTypeQuery.data]);

  const isService = item?.type === 'service';
  const hasComponents = isService || isBundle;
  // Whoever owns this item's cost; the unit-cost field mirrors it read-only.
  const { data: itemSupplierRows = [], isFetched: itemSuppliersFetched } = useItemSuppliers(
    itemTypeCanHaveSuppliers(item?.type) ? id : undefined,
  );
  const primarySupplier = itemSupplierRows.find((row) => row.isPrimary);
  const primarySupplierName = primarySupplier?.supplier?.name;
  // The form's cost is a MIRROR of the primary supplier's, and the suppliers
  // table writes straight to the API — so a cost edited there lands while this
  // form is open and hydrated. Follow it, or the header would keep quoting a
  // cost the supplier no longer charges and the markup beside it would be
  // derived from that same dead number.
  const primarySupplierCost = primarySupplier ? supplierStockCost(primarySupplier) : undefined;
  useEffect(() => {
    if (!itemSuppliersFetched || !itemTypeCanHaveSuppliers(item?.type)) return;
    const effectiveCost = primarySupplierCost ?? 0;
    if (Number(form.getValues('costBasis')) === effectiveCost) return;
    form.setValue('costBasis', effectiveCost, { shouldDirty: false });
  }, [form, item?.type, itemSuppliersFetched, primarySupplierCost]);
  const isMembership = item?.type === 'membership';
  const isMaterial = item?.type === 'material' || item?.type === 'equipment';
  function handlePriceStrategyChange(value: PriceStrategy) {
    if (value === 'FLAT') {
      form.setValue('laborRate', 0, {
        shouldDirty: true,
        shouldValidate: false,
      });
      return;
    }
    // Summing a bundle changes nothing about hours or unit — those belong to
    // the time-and-materials branch below, and applying them to a bundle used
    // to stamp it "HR".
    if (value === 'COMPONENT_SUM') return;
    // The typed flat price is LEFT ALONE. Switching to T&M used to wipe it, so
    // trying the other pricing type and coming back reported the service as
    // free — and the save path already sends 0 for a T&M service, so clearing
    // the field bought nothing.
    form.setValue('costBasis', 0, { shouldDirty: true, shouldValidate: false });
    form.setValue('unit', 'HR', { shouldDirty: true, shouldValidate: false });
  }

  function goBack() {
    void navigate({
      to: '/dashboard/pricebook/$id',
      params: { id },
      search: {} as never,
    });
  }

  // NOT-49: surface exactly which fields are invalid instead of a silent/generic
  // failure. Use the errors RHF hands to onInvalid — `form.formState.errors` is
  // stale at this synchronous point.
  function handleInvalid(errors: FieldErrors<ItemFormValues>) {
    requestAnimationFrame(() => {
      const el = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });

    const issues = collectFieldIssues(errors);
    if (issues.length === 0) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }
    toast.error(issues.length === 1 ? issues[0] : `Please fix ${issues.length} fields`, {
      description: issues.length === 1 ? undefined : issues.join(' • '),
    });
  }

  /** The plan for a save carrying `decision` — nothing here is remembered. */
  function planFor(decision: DependencyDecision) {
    return dynamicSavePlan({
      isService,
      priceStrategy: watchedPriceStrategy,
      usesDynamicPricing,
      dependencyChangeCount: dependencyChanges.length,
      pricingIsStale,
      decision,
    });
  }
  // Only when the changes underneath would actually move a stored figure. A
  // dependency edited without shifting this service — a markup band none of its
  // parts fall in — leaves the item's own page showing no banner, and warning
  // about it here would contradict that over a no-op.
  const showDependencyNotice = planFor('ASK').ask;

  async function onSubmit(values: ItemFormValues) {
    if (!item) return;
    const plan = planFor('ASK');
    if (plan.ask) {
      setPendingSave(values);
      return;
    }
    await saveItem(values, plan.reprice);
  }

  /**
   * @param reprice recalculate the dynamic service's price and cost from its
   * inputs. False only when the operator has chosen to keep the price a changed
   * part would have moved.
   */
  async function saveItem(values: ItemFormValues, reprice: boolean) {
    if (!item) return;
    try {
      const parsed = itemSchema.parse(values);

      // NOT-49: included parts / membership link are tracked outside the zod
      // schema, so guard them here (not by disabling the button) and surface a
      // clear reason.
      if (isMembership && !parsed.membershipPlanId) {
        toast.error('Link a membership plan before saving.');
        return;
      }
      // A concrete equipment item must link an equipment type. `type` isn't part
      // of the edit schema (it's fixed after creation), so guard it here with the
      // known item type. Only block CLEARING an existing link — a pre-existing
      // link-less item (created before the rule) is grandfathered so unrelated
      // edits (price, archive) aren't blocked, mirroring the server.
      const hadLink = !!item.equipmentDescriptionId;
      const clearsLink = hadLink && !parsed.equipmentDescriptionId?.trim();
      if (item.type === 'equipment' && clearsLink) {
        form.setError('equipmentDescriptionId', {
          type: 'manual',
          message: 'Select an equipment type. Create one first if none exist.',
        });
        toast.error('Select an equipment type before saving.');
        return;
      }
      // The category a link-less item's new type is created from is the
      // schema's rule now — see the refine on `equipmentCategoryKey`. Asked
      // there rather than here so the failure marks the field and scrolls to
      // it, like every other invalid field on this form.
      // A T&M service still priced the legacy way must keep a real hourly rate;
      // only a service the user has actually put on dynamic pricing is allowed
      // to have none, because there the labor lines carry the rate instead.
      if (
        isService &&
        parsed.priceStrategy === 'DYNAMIC' &&
        parsed.laborRate <= 0 &&
        !usesDynamicServicePricing(dynamicPricingConfig)
      ) {
        form.setError('laborRate', {
          type: 'manual',
          message: 'Labor rate is required for Time & Materials services.',
        });
        toast.error('Labor rate is required for Time & Materials services.');
        return;
      }

      // Every element of these is validated as a UUID server-side, so one blank
      // left over from a tag that could not be created would reject the whole
      // item — name, price, parts and all — over a selection the user cannot
      // even see. Drop the unusable entries; save what is real.
      const safeTagIds = sanitizeIdList(tagIds);
      const safeCategoryIds = sanitizeIdList(categoryIds);

      // Save the spec panels onto the equipment type they belong to. Done
      // before the item, so a rejected spec stops the save outright instead of
      // reporting success over an edit that never landed.
      let equipmentDescriptionId = parsed.equipmentDescriptionId?.trim() || undefined;
      if (item.type === 'equipment') {
        try {
          if (equipmentDescriptionId) {
            // Only write back a panel that was actually filled FROM the type.
            // Saving blanks would wipe a spec the operator never saw — and
            // skipping quietly would drop whatever they did type, which is the
            // bug this whole change exists to fix. So: say so, and stop.
            if (
              equipmentSpecPendingFor(equipmentDescriptionId, hydratedEquipmentTypeIdRef.current)
            ) {
              toast.error(
                'This item’s equipment specifications haven’t loaded yet. Wait for them to appear, or reload the page, before saving.',
              );
              return;
            }
            await updateEquipmentDescription.mutateAsync({
              id: equipmentDescriptionId,
              input: equipmentTypeSpecFields(parsed) as never,
            });
          } else if (parsed.equipmentCategoryKey) {
            // A grandfathered link-less item, now described: give it the type
            // those specs belong on and link it.
            equipmentDescriptionId = await ensureEquipmentType({
              carriedId: createdEquipmentTypeRef.current,
              spec: newEquipmentTypeSpec(parsed),
              create: (spec) => createEquipmentDescription.mutateAsync(spec as never),
              update: (id, spec) =>
                updateEquipmentDescription.mutateAsync({ id, input: spec as never }),
            });
            createdEquipmentTypeRef.current = equipmentDescriptionId;
          }
        } catch (error) {
          const detail = error instanceof Error ? error.message : 'unknown error';
          toast.error(`Could not save the equipment specification: ${detail}`);
          return;
        }
      }

      const dto: UpdateItemDto = {
        id: item.id,
        name: parsed.name,
        code: parsed.code,
        status: item.status === 'ACTIVE' ? 'ACTIVE' : undefined,
        type: item.type,
        description: parsed.description?.trim() ?? '',
        basePrice: basePriceForUpdate({
          isService,
          priceStrategy: parsed.priceStrategy,
          basePrice: parsed.basePrice,
        }),
        // NOT-605 member pricing (per-item override)
        memberPrice: parsed.memberPrice,
        memberDiscountPercent: parsed.memberDiscountPercent,
        laborRate: isService
          ? parsed.priceStrategy === 'DYNAMIC'
            ? parsed.laborRate
            : 0
          : undefined,
        // Sent for a service in EITHER mode: what an hour costs is true however
        // the price is arrived at, and it is the only thing margin on labor can
        // be read from.
        laborCostRate: isService ? parsed.laborCostRate : undefined,
        estimatedHours: isService ? parsed.estimatedHours : undefined,
        laborCostLines: isService ? parsed.laborCostLines : undefined,
        costBasis: costBasisForUpdate({
          itemType: item.type,
          priceStrategy: parsed.priceStrategy,
          supplierOwned: primarySupplier != null,
          costBasis: parsed.costBasis,
        }),
        markupPercent:
          item.type === 'material' || item.type === 'equipment' ? parsed.markupPercent : undefined,
        currency: parsed.currency?.toUpperCase() || undefined,
        priceStrategy: priceStrategyForItemType(item.type, parsed.priceStrategy),
        // A bundle's config is its own (the members' discount rule); it is not
        // a service's labor config and must survive an edit untouched.
        dynamicPricingConfig: isBundle
          ? dynamicPricingConfig
          : isService && parsed.priceStrategy === 'DYNAMIC'
            ? dynamicPricingConfig
            : { enabled: false },
        unit:
          isService && parsed.priceStrategy === 'DYNAMIC' ? 'HR' : toOptionalString(parsed.unit),
        taxable: parsed.taxable,
        taxCategory: toNullableString(parsed.taxCategory),
        taxCode: toNullableString(parsed.taxCode),
        // As on create: a derived class reads as "Auto" in the form, so it is
        // cleared rather than re-sent. `null` is the update path's "derive it",
        // and the server lands on the same class the item already had.
        taxLineType:
          parsed.taxable === false ? 'exempt' : (storableTaxLineType(parsed.taxLineType) ?? null),
        glAccount: toNullableString(parsed.glAccount),
        glClass: toNullableString(parsed.glClass),
        glCogs: toNullableString(parsed.glCogs),
        glExpenseAccount: toNullableString(parsed.glExpenseAccount),
        displayOrder: parsed.displayOrder || undefined,
        displayConfig: {
          hidePrice: parsed.hidePrice,
          requirePhotos: parsed.requirePhotos,
          requireAsset: parsed.requireAsset,
          lineDetail: parsed.lineDetail,
        },
        ...buildAiSearchUpdateFields(parsed),
        equipmentDescriptionId: item.type === 'equipment' ? (equipmentDescriptionId ?? null) : null,
        isConfigurable: item.type === 'equipment' ? parsed.isConfigurable : false,
        configurations:
          item.type === 'equipment' && parsed.isConfigurable ? parsed.configurations : [],
        hazardLevel: parsed.hazardLevel,
        retrievalBoost: parsed.retrievalBoost !== 1 ? parsed.retrievalBoost : undefined,
        tagIds: safeTagIds,
        // Always sent — an empty array is how "take it out of every folder"
        // reaches the server. Omitting it would make un-filing impossible.
        // EVERY folder goes, not just the primary: the server replaces the
        // whole membership set, so sending one id would unfile the rest.
        categoryIds: safeCategoryIds,
        // The picker keeps the primary first, so this names the folder shown
        // as Primary. Sent explicitly rather than leaning on the server's
        // "defaults to the first".
        primaryCategoryId: safeCategoryIds[0],
      };

      // NOT-590 — always send the list on a SERVICE, so clearing the last part
      // actually clears it instead of silently leaving the old one attached.
      if (hasComponents) {
        // Built field by field rather than spread: the API forbids keys it does
        // not declare, and an out-of-union value fails the whole save.
        dto.components = toServiceComponentPayloads(components, !isService);
        dto.componentDisplayMode = isService
          ? 'ROLLED_UP'
          : (parsed.componentDisplayMode ?? 'ROLLED_UP');
      }
      if (isMembership) {
        if (parsed.membershipPlanId) {
          dto.membershipConfig = {
            planId: parsed.membershipPlanId,
            grantTierId: toNullableString(parsed.membershipGrantTierId),
          };
        }
        if (parsed.membershipPlanIds?.length) dto.membershipPlanIds = parsed.membershipPlanIds;
      }
      // Always send (even empty) so clearing items persists on edit.
      dto.upsellItems = upsellItems.filter((u) => u.itemId);

      if (pendingImageFile) {
        try {
          const fileKey = await uploadImage.mutateAsync({
            id: item.id,
            file: pendingImageFile,
          });
          dto.imageUrl = fileKey;
        } catch (uploadError) {
          // NOT-53: surface the underlying upload error (S3 status + body,
          // or backend 4xx). Otherwise the save silently aborts here and
          // the user just sees the form fail to submit.
          const detail = uploadError instanceof Error ? uploadError.message : 'unknown error';
          toast.error(`Image upload failed: ${detail}`);
          return;
        }
      }

      await updateItem.mutateAsync(dto);

      // RECALCULATE what the save just changed the inputs to.
      //
      // A dynamic service's price and cost are derived, and this save says
      // nothing about either (see `basePriceForUpdate` / `costBasisForUpdate`)
      // — so without this the operator configured labor and parts, watched the
      // panel total them, saved, and found the catalog still listing the price
      // the service had before, against a cost of $0. The components are part
      // of the same request above, so the calculation here sees the finished
      // service.
      //
      // Best-effort, exactly as at create: the edit landed and is correct apart
      // from this figure, so a failure here is worth a warning rather than
      // pretending the save itself failed.
      if (reprice && isService && parsed.priceStrategy === 'DYNAMIC' && usesDynamicPricing) {
        try {
          await applyDynamicPrice.mutateAsync(item.id);
        } catch {
          toast.warning(
            'Saved, but the price could not be recalculated. Open the item and choose Update service pricing.',
          );
        }
      }
      // Saved — this redirect must not be met with "you have unsaved changes",
      // as the form still reads dirty until it unmounts.
      unsavedChanges.allowNextNavigation();
      goBack();
    } catch (error) {
      if (isApiError(error)) {
        if (error.fieldErrors) applyServerErrorsToForm(form, error.fieldErrors);
        return;
      }
      throw error;
    }
  }

  if (itemQuery.isLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 flex items-center gap-3 px-4 sm:px-6 py-3 border-b bg-background">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goBack}>
            <ArrowLeft size={16} />
          </Button>
          <Skeleton className="h-6 w-32" />
        </div>
        <div className="flex-1 px-4 sm:px-6 py-4 space-y-3">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
        <p className="text-sm">Item not found</p>
        <Button variant="outline" size="sm" onClick={goBack}>
          Back to Pricebook
        </Button>
      </div>
    );
  }

  // NOT-49: the Save button stays enabled while the form is invalid so a click
  // reaches `handleInvalid` and surfaces field-specific errors — a disabled
  // button would leave the user with no explanation. It only blocks while a
  // mutation is in flight (`isMutating`, above).
  const hasOptionalDetails =
    Boolean(watchedDescription?.trim()) || tagIds.length > 0 || Boolean(imagePreview);
  const priceSummaryItem = {
    type: item.type,
    price: Number(watchedBasePrice || 0),
    laborRate: Number(watchedLaborRate || 0),
    priceStrategy: watchedPriceStrategy,
  };
  const listedPrice = pricebookPrimaryPrice(priceSummaryItem);
  const listedPriceLabel = pricebookPrimaryPriceLabel(priceSummaryItem);
  const listedPriceSuffix = pricebookPrimaryPriceSuffix(priceSummaryItem);
  const namePlaceholder = isService
    ? 'e.g. AC Tune-Up'
    : isMembership
      ? 'e.g. Comfort Care Membership'
      : hasComponents
        ? 'e.g. HVAC Comfort Bundle'
        : isMaterial
          ? 'e.g. Copper Pipe 1/2"'
          : 'e.g. Item name';
  const codePlaceholder = isService
    ? 'e.g. SVC-AC-001'
    : isMembership
      ? 'e.g. MEM-GOLD-001'
      : hasComponents
        ? 'e.g. PKG-HVAC-001'
        : 'e.g. MAT-CU-012';

  return (
    <TooltipProvider>
      <FormPageLayout
        title={`Edit ${ITEM_TYPE_LABELS[item.type] ?? 'Item'}`}
        onBack={goBack}
        noscroll
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={goBack}
              disabled={updateItem.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit, handleInvalid)}
              disabled={isMutating}
            >
              {isMutating && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Save
            </Button>
          </>
        }
      >
        <div className="flex h-full min-h-0">
          <Form {...form}>
            <form
              ref={formRef}
              onSubmit={form.handleSubmit(onSubmit, handleInvalid)}
              className="flex h-full min-h-0 min-w-0 flex-1"
            >
              <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="space-y-4 px-4 py-4">
                  {/* Say it where the price is being edited, not only on the
                      item's page. The summary on the right recalculates from
                      the parts as they stand NOW, so without this the new
                      figure simply appears there — indistinguishable from the
                      operator's own arithmetic — and the first they learn a
                      material moved is the price on the list afterwards. */}
                  {showDependencyNotice && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-950">
                      <p className="text-sm font-semibold">
                        A part this service is priced from has changed
                      </p>
                      <ul className="mt-1.5 space-y-0.5 text-sm">
                        {dependencyChanges.map((change, index) => (
                          <li key={`${change.kind}-${change.label}-${index}`}>
                            {change.label}:{' '}
                            <span className="line-through opacity-70">{change.before}</span>{' '}
                            <span aria-hidden>→</span>{' '}
                            <span className="font-semibold">{change.after}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1.5 text-sm">
                        Saving will ask before using{' '}
                        {dependencyChanges.length === 1 ? 'it' : 'them'}.
                      </p>
                    </div>
                  )}

                  <PricebookItemMainSection
                    form={form}
                    itemType={item.type}
                    namePlaceholder={namePlaceholder}
                    codePlaceholder={codePlaceholder}
                    codeHint="Unique identifier for this item"
                    open={basicInfoOpen}
                    onToggle={() => setBasicInfoOpen((v) => !v)}
                    onPriceStrategyChange={handlePriceStrategyChange}
                    primarySupplierName={primarySupplierName}
                    currentItemId={id}
                    tagIds={tagIds}
                    onTagIdsChange={setTagIds}
                    categoryIds={categoryIds}
                    onCategoryIdsChange={setCategoryIds}
                    imagePreview={imagePreview}
                    onSelectImage={(file) => void selectImage(file)}
                    onClearImage={clearImage}
                    equipmentSpecPending={equipmentSpecPending}
                    header={
                      <div className="flex items-center gap-2">
                        <ItemTypeBadge type={item.type} />
                        <span className="text-xs text-muted-foreground">
                          Type cannot be changed
                        </span>
                      </div>
                    }
                  />

                  {isService && watchedPriceStrategy === 'DYNAMIC' && (
                    <>
                      <DynamicServicePricingEditor
                        value={dynamicPricingConfig}
                        onChange={setDynamicPricingConfig}
                      />
                      <ProfitUpliftEditor
                        value={dynamicPricingConfig}
                        onChange={setDynamicPricingConfig}
                        amount={dynamicSummary.surcharge}
                        base={dynamicSummary.surchargeBase}
                      />
                    </>
                  )}

                  {(hasComponents || isMembership) && (
                    <PricebookItemAdvancedSections
                      form={form}
                      itemType={item.type}
                      isService={isService}
                      hasComponents={hasComponents}
                      isMembership={isMembership}
                      watchedTaxable={Boolean(watchedTaxable)}
                      components={components}
                      onComponentsChange={setComponents}
                      componentItemCache={componentItemCache}
                      onComponentItemCacheChange={setComponentItemCache}
                      materialMarkupProfileId={dynamicPricingConfig.materialMarkupProfileId}
                      equipmentMarkupProfileId={dynamicPricingConfig.equipmentMarkupProfileId}
                      materialPricingMethod={dynamicPricingConfig.materialPricingMethod}
                      equipmentPricingMethod={dynamicPricingConfig.equipmentPricingMethod}
                      useSameMarkupProfile={dynamicPricingConfig.useSameMarkupProfile}
                      onMaterialMarkupProfileChange={(
                        materialMarkupProfileId,
                        materialPricingMethod,
                      ) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          materialMarkupProfileId,
                          materialPricingMethod,
                        }))
                      }
                      onEquipmentMarkupProfileChange={(
                        equipmentMarkupProfileId,
                        equipmentPricingMethod,
                      ) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          equipmentMarkupProfileId,
                          equipmentPricingMethod,
                        }))
                      }
                      onUseSameMarkupProfileChange={(useSameMarkupProfile) =>
                        setDynamicPricingConfig((current) => ({
                          ...current,
                          useSameMarkupProfile,
                        }))
                      }
                      upsellItems={upsellItems}
                      onUpsellItemsChange={setUpsellItems}
                      currentItemId={id}
                      componentsSectionOpen={componentsSectionOpen}
                      onComponentsSectionToggle={() => setComponentsSectionOpen((v) => !v)}
                      membershipLinkOpen={membershipLinkOpen}
                      onMembershipLinkToggle={() => setMembershipLinkOpen((v) => !v)}
                      taxOpen={taxOpen}
                      onTaxToggle={() => setTaxOpen((v) => !v)}
                      aiOpen={aiOpen}
                      onAiToggle={() => setAiOpen((v) => !v)}
                      displayOpen={displayOpen}
                      onDisplayToggle={() => setDisplayOpen((v) => !v)}
                      includeOptionalSections={false}
                    />
                  )}

                  {isService && watchedPriceStrategy === 'DYNAMIC' && (
                    <AdvancedServiceAdjustments
                      value={dynamicPricingConfig}
                      onChange={setDynamicPricingConfig}
                    />
                  )}

                  <PricebookItemAdvancedSections
                    form={form}
                    itemType={item.type}
                    isService={isService}
                    hasComponents={hasComponents}
                    isMembership={isMembership}
                    watchedTaxable={Boolean(watchedTaxable)}
                    components={components}
                    onComponentsChange={setComponents}
                    componentItemCache={componentItemCache}
                    onComponentItemCacheChange={setComponentItemCache}
                    materialMarkupProfileId={dynamicPricingConfig.materialMarkupProfileId}
                    equipmentMarkupProfileId={dynamicPricingConfig.equipmentMarkupProfileId}
                    materialPricingMethod={dynamicPricingConfig.materialPricingMethod}
                    equipmentPricingMethod={dynamicPricingConfig.equipmentPricingMethod}
                    useSameMarkupProfile={dynamicPricingConfig.useSameMarkupProfile}
                    onMaterialMarkupProfileChange={(
                      materialMarkupProfileId,
                      materialPricingMethod,
                    ) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        materialMarkupProfileId,
                        materialPricingMethod,
                      }))
                    }
                    onEquipmentMarkupProfileChange={(
                      equipmentMarkupProfileId,
                      equipmentPricingMethod,
                    ) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        equipmentMarkupProfileId,
                        equipmentPricingMethod,
                      }))
                    }
                    onUseSameMarkupProfileChange={(useSameMarkupProfile) =>
                      setDynamicPricingConfig((current) => ({
                        ...current,
                        useSameMarkupProfile,
                      }))
                    }
                    upsellItems={upsellItems}
                    onUpsellItemsChange={setUpsellItems}
                    currentItemId={id}
                    componentsSectionOpen={componentsSectionOpen}
                    onComponentsSectionToggle={() => setComponentsSectionOpen((v) => !v)}
                    membershipLinkOpen={membershipLinkOpen}
                    onMembershipLinkToggle={() => setMembershipLinkOpen((v) => !v)}
                    taxOpen={taxOpen}
                    onTaxToggle={() => setTaxOpen((v) => !v)}
                    aiOpen={aiOpen}
                    onAiToggle={() => setAiOpen((v) => !v)}
                    displayOpen={displayOpen}
                    onDisplayToggle={() => setDisplayOpen((v) => !v)}
                    includeRequiredSections={false}
                  />
                </div>
              </div>

              <FormSidebar
                checklist={pricebookItemChecklist({
                  // An item's type is fixed at creation, so this one is settled
                  // before the page opens.
                  typeDone: true,
                  name: watchedName,
                  code: watchedCode,
                  equipmentCategory: {
                    isEquipment: item.type === 'equipment',
                    equipmentDescriptionId: watchedEquipmentDescriptionId,
                    equipmentCategoryKey: watchedEquipmentCategoryKey,
                    writesEquipmentType: hasTypedEquipmentSpec,
                  },
                  isMembership,
                  membershipPlanId: watchedMembershipPlanId,
                  hasOptionalDetails,
                })}
              >
                <FormSidebarSection label="Summary">
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Type</span>
                      <span>{ITEM_TYPE_LABELS[item.type]}</span>
                    </div>
                    {isService && isTimeAndMaterials ? (
                      <>
                        {/* The same panel the estimate shows, so a service
                            priced here and the same service quoted on a
                            document read as one idea rather than two. */}
                        <DocumentCostPanel
                          className="border-0 bg-transparent p-0"
                          subtotal={dynamicSummary.calculatedPrice}
                          taxTotal={0}
                          grandTotal={dynamicSummary.calculatedPrice}
                          breakdown={{
                            labor: dynamicSummary.laborCost,
                            laborHours: Math.round((dynamicSummary.totalMinutes / 60) * 100) / 100,
                            material: dynamicSummary.materialCost,
                            equipment: dynamicSummary.equipmentCost,
                          }}
                        />
                        <SummaryRow
                          label="Labor"
                          value={fmt(dynamicSummary.labor)}
                          suffix={`${Math.floor(dynamicSummary.totalMinutes / 60)}h ${dynamicSummary.totalMinutes % 60}m`}
                        />
                        {/* Every part contributes to the calculated price,
                            whether the service covers it or bills it extra —
                            so this figure is "parts", not "included parts". */}
                        <SummaryRow label="Parts" value={fmt(dynamicSummary.parts)} />
                        <SummaryRow
                          label="Calculated cost"
                          value={fmt(dynamicSummary.calculatedCost)}
                          muted
                        />
                        {dynamicSummary.aggregateMarkup != null && (
                          <SummaryRow
                            label="Parts markup"
                            value={`${dynamicSummary.aggregateMarkup.toFixed(1)}%`}
                            muted
                          />
                        )}
                        {dynamicSummary.complexity > 0 && (
                          <SummaryRow
                            label="Complexity"
                            value={fmt(dynamicSummary.complexity)}
                            muted
                          />
                        )}
                        {dynamicSummary.surcharge !== 0 && (
                          <SummaryRow
                            label="Profit uplift"
                            value={fmt(dynamicSummary.surcharge)}
                            muted
                          />
                        )}
                        <div className="mt-2 flex justify-between gap-3 border-t pt-2 font-semibold">
                          <span>Calculated price</span>
                          <span className="tabular-nums">
                            {fmt(dynamicSummary.calculatedPrice)}
                          </span>
                        </div>

                        {/* WHAT-IF, not configuration. Both rules are already
                            switched on or off in the pricing editor; these ask
                            the different question of what they DO, which the
                            everyday price cannot show because neither applies
                            to an everyday job. Nothing here is saved, and the
                            Calculated price above never moves. */}
                        <div className="mt-3 space-y-2 border-t pt-3">
                          {/* Styled as the Cost and Margin groups are: an
                              uppercase group label over plain rows. The dashed
                              box it replaced read as a drop zone rather than a
                              part of the summary. */}
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Scenarios
                          </p>
                          <label className="flex items-center justify-between gap-3 text-xs">
                            <span
                              className={
                                dynamicPricingConfig.afterHours?.enabled
                                  ? ''
                                  : 'text-muted-foreground'
                              }
                            >
                              …the work is after hours
                              {!dynamicPricingConfig.afterHours?.enabled && ' (not set up)'}
                            </span>
                            <Switch
                              aria-label="Preview after-hours pricing"
                              disabled={!dynamicPricingConfig.afterHours?.enabled}
                              checked={previewAfterHours}
                              onCheckedChange={setPreviewAfterHours}
                            />
                          </label>
                          <label className="flex items-center justify-between gap-3 text-xs">
                            <span
                              className={
                                dynamicPricingConfig.addOnDiscount?.enabled
                                  ? ''
                                  : 'text-muted-foreground'
                              }
                            >
                              …it is sold as an add-on
                              {!dynamicPricingConfig.addOnDiscount?.enabled && ' (not set up)'}
                            </span>
                            <Switch
                              aria-label="Preview add-on discount"
                              disabled={!dynamicPricingConfig.addOnDiscount?.enabled}
                              checked={previewAsAddOn}
                              onCheckedChange={setPreviewAsAddOn}
                            />
                          </label>

                          {previewAfterHours || previewAsAddOn ? (
                            <div className="space-y-1 border-t pt-2">
                              {adjustmentPreview.afterHoursAdjustment !== 0 && (
                                <SummaryRow
                                  label="After hours"
                                  value={`+${fmt(adjustmentPreview.afterHoursAdjustment)}`}
                                  muted
                                />
                              )}
                              {adjustmentPreview.addOnDiscount !== 0 && (
                                <SummaryRow
                                  label="Add-on discount"
                                  value={`-${fmt(adjustmentPreview.addOnDiscount)}`}
                                  muted
                                />
                              )}
                              <div className="flex justify-between gap-3 font-semibold">
                                <span>Price in that case</span>
                                <span className="tabular-nums">{fmt(adjustmentPreview.total)}</span>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : isService ? (
                      <>
                        {/* PRICE FIRST, then what it costs and what is left.

                            Two different kinds of statement: what the CUSTOMER PAYS

                            (this service's price, the parts billed beside it, their

                            total) and what the BUSINESS KEEPS (cost, margin, net).

                            Drawn the other way round, Gross margin and Net amount sat

                            in the middle of the price composition and the column read

                            as one undifferentiated list of money. */}
                        <PricebookBundleSummary
                          listedPrice={listedPrice}
                          listedPriceLabel={listedPriceLabel}
                          listedPriceSuffix={listedPriceSuffix}
                          totals={componentTotals}
                          showBundleTotal={showBundleTotal}
                          totalLabel="Total (service + parts)"
                        />

                        {/* The same panel a T&M service gets: how a service is priced

                            changes the figures, not what the operator needs to see.

                            Its own price rows are suppressed because the summary above

                            already states them — and better, since it breaks them

                            down. Margin is still taken against that same total. */}

                        <DocumentCostPanel
                          className="border-0 bg-transparent p-0"
                          showPriceSummary={false}
                          subtotal={componentTotals.bundleTotal}
                          taxTotal={0}
                          grandTotal={componentTotals.bundleTotal}
                          breakdown={flatServiceCost}
                        />
                      </>
                    ) : hasComponents ? (
                      <PricebookBundleSummary
                        listedPrice={listedPrice}
                        listedPriceLabel={listedPriceLabel}
                        listedPriceSuffix={listedPriceSuffix}
                        totals={componentTotals}
                        showBundleTotal={showBundleTotal}
                        totalLabel={isBundle ? 'Bundle total' : 'Total (service + parts)'}
                      />
                    ) : !isMembership ? (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">{listedPriceLabel}</span>
                        <span className="tabular-nums">
                          {fmt(listedPrice)}
                          {listedPriceSuffix && (
                            <span className="ml-0.5 text-xs text-muted-foreground">
                              {listedPriceSuffix}
                            </span>
                          )}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Tags</span>
                      <span>{tagIds.length}</span>
                    </div>
                    {hasComponents && components.length > 0 && (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Parts</span>
                        <span>{components.length}</span>
                      </div>
                    )}
                    {isMembership && (
                      <div className="flex justify-between gap-3">
                        <span className="text-muted-foreground">Plan</span>
                        <span>{watchedMembershipPlanId ? 'Linked' : '—'}</span>
                      </div>
                    )}
                  </div>
                </FormSidebarSection>
                {!isMembership && !hasComponents && (
                  <FormSidebarSection label="Member Pricing" border>
                    <MembershipPricingBreakdown
                      basePrice={listedPrice}
                      itemType={item.type}
                      itemId={id}
                    />
                  </FormSidebarSection>
                )}
              </FormSidebar>
            </form>
          </Form>
        </div>
        <UnsavedChangesDialog
          open={unsavedChanges.isBlocked}
          onStay={unsavedChanges.stay}
          onLeave={unsavedChanges.leave}
          description="Your edits to this item have not been saved. Leaving now will discard them."
        />
        <DependencyChangeDialog
          open={pendingSave != null}
          serviceName={item.name}
          changes={dependencyChanges}
          currentPrice={dynamicPreview.data?.currentPrice ?? item.price ?? 0}
          calculatedPrice={dynamicPreview.data?.calculatedPrice ?? 0}
          busy={isMutating}
          onCancel={() => setPendingSave(null)}
          // The answer travels with THIS save and is not remembered. A decision
          // kept in state goes on answering for edits made after it: decline a
          // part's new cost, then change a labor line, and that edit would be
          // left unpriced too.
          onUseUpdated={() => {
            const values = pendingSave;
            setPendingSave(null);
            if (values) void saveItem(values, planFor('REPRICE').reprice);
          }}
          onKeepPrice={() => {
            const values = pendingSave;
            setPendingSave(null);
            if (values) void saveItem(values, planFor('KEEP').reprice);
          }}
        />
      </FormPageLayout>
    </TooltipProvider>
  );
}

function SummaryRow({
  label,
  value,
  suffix,
  muted,
}: {
  label: string;
  value: string;
  suffix?: string;
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={muted ? 'tabular-nums text-muted-foreground' : 'tabular-nums'}>
        {value}
        {suffix && <span className="ml-1 text-xs text-muted-foreground">({suffix})</span>}
      </span>
    </div>
  );
}
